import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { items, detailed, subtotal, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [config, setConfig] = useState(null);
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [applying, setApplying] = useState(false);
  const [discount, setDiscount] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const discountAmount = discount ? Math.min(discount.discount_amount, subtotal) : 0;
  const effSubtotal = Math.max(0, subtotal - discountAmount);
  const shipping = effSubtotal === 0 || effSubtotal >= 999 ? 0 : 79;
  const total = Math.max(subtotal > 0 ? 1 : 0, effSubtotal + shipping);

  useEffect(() => {
    axios.get(`${API}/checkout/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);

  useEffect(() => {
    if (!discount || items.length === 0) {
      if (discount && items.length === 0) setDiscount(null);
      return;
    }
    axios
      .post(`${API}/checkout/validate-discount`, { code: discount.code, items })
      .then(({ data }) => setDiscount(data))
      .catch((err) => {
        setDiscount(null);
        setCodeInput("");
        toast.error(err.response?.data?.detail || "Discount code no longer applies to your cart.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const applyCode = async () => {
    if (!codeInput.trim()) return;
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/checkout/validate-discount`, { code: codeInput.trim(), items });
      setDiscount(data);
      toast.success(`Code ${data.code} applied — you save ${inr(data.discount_amount)}`);
    } catch (err) {
      setDiscount(null);
      toast.error(err.response?.data?.detail || "Invalid discount code.");
    } finally {
      setApplying(false);
    }
  };

  const removeCode = () => {
    setDiscount(null);
    setCodeInput("");
  };

  const pay = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/checkout/create-order`, {
        items,
        customer: form,
        discount_code: discount?.code || null,
      });
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load payment gateway");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "Reserve 30 Parfums",
        description: "Luxury fragrances crafted to leave a mark",
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#BA8D53" },
        handler: async (res) => {
          try {
            await axios.post(`${API}/checkout/verify`, {
              order_id: data.order_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            clear();
            setSuccess({ orderId: data.order_id, total: data.total });
          } catch {
            toast.error("Payment verification failed. Contact us with your payment ID.");
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment. Please try again.");
      setPaying(false);
    }
  };

  if (success) {
    return (
      <main data-testid="order-success" className="pt-32 min-h-screen">
        <div className="max-w-lg mx-auto px-5 text-center">
          <CheckCircle2 size={44} strokeWidth={1} className="mx-auto text-[#BA8D53]" />
          <h1 className="font-serif-brand text-4xl md:text-5xl font-light mt-6">Order confirmed.</h1>
          <p className="mt-4 text-sm text-black/60 leading-relaxed">
            Thank you — your payment of <strong>{inr(success.total)}</strong> was received. A confirmation is on its way to your email.
          </p>
          <p className="mt-2 text-[0.65rem] uppercase tracking-[0.24em] text-black/40">Order {success.orderId.slice(0, 8).toUpperCase()}</p>
          <p className="mt-4 text-xs text-black/50">
            Save this Order ID — you can check your order anytime on the{" "}
            <Link to="/track-order" data-testid="success-track-link" className="underline text-[#BA8D53]">Track Order</Link> page.
          </p>
          <Link to="/collections" data-testid="success-continue-link" className="btn-ink mt-10">Continue Shopping</Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="pt-24 md:pt-32">
      <Seo title="Checkout — Reserve 30 Parfums" description="Secure checkout for Reserve 30 Parfums." />
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 pb-28">
        <Reveal>
          <p className="overline-label">Secure Checkout</p>
          <h1 className="font-serif-brand text-5xl md:text-6xl font-light mt-4">Checkout</h1>
        </Reveal>

        {items.length === 0 ? (
          <div className="mt-16" data-testid="checkout-empty">
            <p className="text-black/50">Your cart is empty.</p>
            <Link to="/collections" className="btn-ink mt-6">Shop Collection</Link>
          </div>
        ) : (
          <div className="mt-14 grid md:grid-cols-12 gap-14">
            <form onSubmit={pay} className="md:col-span-7 space-y-7" data-testid="checkout-form">
              <input required data-testid="checkout-name" className="input-minimal" placeholder="Full name" value={form.name} onChange={set("name")} />
              <div className="grid sm:grid-cols-2 gap-7">
                <input required type="email" data-testid="checkout-email" className="input-minimal" placeholder="Email" value={form.email} onChange={set("email")} />
                <input required type="tel" pattern="[0-9+ -]{10,}" data-testid="checkout-phone" className="input-minimal" placeholder="Phone" value={form.phone} onChange={set("phone")} />
              </div>
              <input required data-testid="checkout-address" className="input-minimal" placeholder="Address" value={form.address} onChange={set("address")} />
              <div className="grid sm:grid-cols-3 gap-7">
                <input required data-testid="checkout-city" className="input-minimal" placeholder="City" value={form.city} onChange={set("city")} />
                <input required data-testid="checkout-state" className="input-minimal" placeholder="State" value={form.state} onChange={set("state")} />
                <input required pattern="[0-9]{6}" data-testid="checkout-pincode" className="input-minimal" placeholder="Pincode" value={form.pincode} onChange={set("pincode")} />
              </div>

              {config && !config.configured && (
                <p data-testid="payments-not-configured-notice" className="text-sm text-[#BA8D53] border border-[#BA8D53]/40 p-4 leading-relaxed">
                  Online payments are being activated. Please check back shortly — or reach us at contact@reserve30.in to order directly.
                </p>
              )}

              <button
                type="submit"
                disabled={paying || !config?.configured}
                data-testid="checkout-pay-button"
                className="btn-ink disabled:opacity-40 w-full sm:w-auto justify-center"
              >
                {paying ? <Loader2 size={14} className="animate-spin" /> : null}
                {paying ? "Processing…" : `Pay ${inr(total)} with Razorpay`}
              </button>
              <p className="text-[0.62rem] uppercase tracking-[0.2em] text-black/40">UPI · Cards · Netbanking · Wallets — secured by Razorpay</p>
            </form>

            <div className="md:col-span-5">
              <div className="border border-black/10 bg-white/40 p-6" data-testid="checkout-summary">
                <p className="overline-label mb-5">Order Summary</p>
                <div className="space-y-4">
                  {detailed.map((i) => (
                    <div key={`${i.slug}-${i.variant}`} className="flex gap-3 items-center">
                      <img src={i.image} alt={i.name} className="w-12 h-14 object-cover bg-[#EFE9E1]" />
                      <div className="flex-1">
                        <p className="font-serif-brand leading-tight">{i.name}</p>
                        <p className="text-[0.6rem] uppercase tracking-[0.18em] text-black/45">{i.variantLabel} × {i.qty}</p>
                      </div>
                      <span className="text-sm font-medium">{inr(i.lineTotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-black/10">
                  {discount ? (
                    <div className="flex items-center justify-between text-sm" data-testid="discount-applied-row">
                      <span className="text-[#BA8D53] font-medium">{discount.code} applied</span>
                      <button type="button" onClick={removeCode} data-testid="discount-remove-button" className="text-black/40 hover:text-black text-xs uppercase tracking-[0.15em]">
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        data-testid="discount-code-input"
                        className="input-minimal flex-1 !text-sm uppercase"
                        placeholder="Discount code"
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                      />
                      <button
                        type="button"
                        onClick={applyCode}
                        disabled={applying || !codeInput.trim()}
                        data-testid="discount-apply-button"
                        className="btn-ghost-ink !px-4 !py-2 text-xs disabled:opacity-40 shrink-0"
                      >
                        {applying ? <Loader2 size={12} className="animate-spin" /> : "Apply"}
                      </button>
                    </div>
                  )}
                </div>
                <div className="mt-6 pt-4 border-t border-black/10 space-y-2 text-sm">
                  <div className="flex justify-between text-black/60"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-[#BA8D53]" data-testid="checkout-discount-row">
                      <span>Discount ({discount.code})</span><span>−{inr(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-black/60"><span>Shipping</span><span>{shipping === 0 ? "Free" : inr(shipping)}</span></div>
                  <div className="flex justify-between font-medium text-base pt-2 border-t border-black/10"><span>Total</span><span data-testid="checkout-total">{inr(total)}</span></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
